import {
    Activity,
    BadgeCheck,
    BarChart3,
    Bell,
    Boxes,
    Briefcase,
    Calendar,
    CheckCircle2,
    ClipboardList,
    CreditCard,
    FileText,
    Inbox,
    Layers,
    LayoutTemplate,
    Link2,
    ListChecks,
    ListOrdered,
    Mail,
    Megaphone,
    Monitor,
    PenLine,
    PieChart,
    Scale,
    Shield,
    ShieldCheck,
    ShoppingBag,
    Sparkles,
    Tag,
    UserPlus,
    Wallet,
    Zap,
    type LucideIcon,
} from 'lucide-react'
import type { TemplateKind } from './types'

/** Persian UI copy for the templates catalog page (website-fa). */
export const templatesPageCopy = {
    heroTitle: 'یک قالب انتخاب کنید',
    heroSubtitle: 'مجموعه قالب‌های آماده را مرور کنید و سریع‌تر شروع کنید.',
    filtersTitle: 'فیلترها',
    clearFilters: 'پاک کردن',
    filterTemplateType: 'نوع قالب',
    filterCategories: 'دسته‌ها',
    filterTags: 'تگ‌ها',
    searchPlaceholder: 'جستجو بر اساس نام قالب…',
    searchAriaLabel: 'جستجوی قالب‌ها',
    resultCount: (count: number) => `${count.toLocaleString('fa-IR')} قالب`,
    emptyTitle: 'قالبی با این فیلترها پیدا نشد',
    emptyHint: 'عبارت جستجو را عوض کنید یا بعضی فیلترها را بردارید.',
    emptyClearButton: 'پاک کردن همه فیلترها',
} as const

const categoryLabelsFa: Record<string, string> = {
    Forms: 'فرم‌ها',
    Documents: 'اسناد',
    'Customer Onboarding': 'ورود مشتری',
    'Sales Documents': 'اسناد فروش',
    Marketing: 'بازاریابی',
    HR: 'منابع انسانی',
    Finance: 'مالی',
    IT: 'فناوری اطلاعات',
    Legal: 'حقوقی',
    Other: 'سایر',
}

export function categoryLabelFa(category: string): string {
    return categoryLabelsFa[category] ?? category
}

const tagLabelsFa: Record<string, string> = {
    Email: 'ایمیل',
    Payments: 'پرداخت',
    'E-signature': 'امضای الکترونیک',
    Approval: 'تأیید',
    Automation: 'خودکارسازی',
    Analytics: 'تحلیل',
    Calendar: 'تقویم',
    Workflow: 'گردش کار',
    Tracking: 'پیگیری',
    Integration: 'یکپارچه‌سازی',
    Security: 'امنیت',
    PDF: 'PDF',
    Reporting: 'گزارش',
    Verification: 'تأیید هویت',
    Notifications: 'اعلان',
    'Multi-step': 'چندمرحله‌ای',
    Compliance: 'انطباق',
}

export function tagLabelFa(tag: string): string {
    return tagLabelsFa[tag] ?? tag
}

const templateKindLabelsFa: Record<TemplateKind, string> = {
    Form: 'فرم',
    Survey: 'نظرسنجی',
    Document: 'سند',
    Checklist: 'چک‌لیست',
    Request: 'درخواست',
    Other: 'سایر',
}

export function templateKindLabelFa(kind: TemplateKind): string {
    return templateKindLabelsFa[kind]
}

export const templatePreviewCopyFa = {
    keyFeatures: 'ویژگی‌های کلیدی',
    useTemplate: 'استفاده از قالب',
    closeAriaLabel: 'بستن',
} as const

const categoryIcons: Record<string, LucideIcon> = {
    Forms: ClipboardList,
    Documents: FileText,
    'Customer Onboarding': UserPlus,
    'Sales Documents': ShoppingBag,
    Marketing: Megaphone,
    HR: Briefcase,
    Finance: Wallet,
    IT: Monitor,
    Legal: Scale,
    Other: Sparkles,
}

export function categoryIconFa(category: string): LucideIcon {
    return categoryIcons[category] ?? Boxes
}

const categoryAccents: Record<string, string> = {
    Forms: 'text-sky-600 bg-sky-50 dark:bg-sky-500/15 dark:text-sky-300',
    Documents: 'text-indigo-600 bg-indigo-50 dark:bg-indigo-500/15 dark:text-indigo-300',
    'Customer Onboarding': 'text-emerald-600 bg-emerald-50 dark:bg-emerald-500/15 dark:text-emerald-300',
    'Sales Documents': 'text-amber-600 bg-amber-50 dark:bg-amber-500/15 dark:text-amber-300',
    Marketing: 'text-pink-600 bg-pink-50 dark:bg-pink-500/15 dark:text-pink-300',
    HR: 'text-violet-600 bg-violet-50 dark:bg-violet-500/15 dark:text-violet-300',
    Finance: 'text-green-600 bg-green-50 dark:bg-green-500/15 dark:text-green-300',
    IT: 'text-cyan-600 bg-cyan-50 dark:bg-cyan-500/15 dark:text-cyan-300',
    Legal: 'text-slate-600 bg-slate-100 dark:bg-slate-500/15 dark:text-slate-300',
    Other: 'text-fuchsia-600 bg-fuchsia-50 dark:bg-fuchsia-500/15 dark:text-fuchsia-300',
}

export function categoryAccentFa(category: string): string {
    return categoryAccents[category] ?? 'text-muted-foreground bg-muted'
}

const templateKindIcons: Record<TemplateKind, LucideIcon> = {
    Form: ClipboardList,
    Survey: BarChart3,
    Document: FileText,
    Checklist: ListChecks,
    Request: Inbox,
    Other: Layers,
}

export function templateKindIconFa(kind: TemplateKind): LucideIcon {
    return templateKindIcons[kind]
}

const templateKindAccents: Record<TemplateKind, string> = {
    Form: 'text-sky-600 bg-sky-50 dark:bg-sky-500/15 dark:text-sky-300',
    Survey: 'text-amber-600 bg-amber-50 dark:bg-amber-500/15 dark:text-amber-300',
    Document: 'text-indigo-600 bg-indigo-50 dark:bg-indigo-500/15 dark:text-indigo-300',
    Checklist: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-500/15 dark:text-emerald-300',
    Request: 'text-rose-600 bg-rose-50 dark:bg-rose-500/15 dark:text-rose-300',
    Other: 'text-slate-600 bg-slate-100 dark:bg-slate-500/15 dark:text-slate-300',
}

export function templateKindAccentFa(kind: TemplateKind): string {
    return templateKindAccents[kind]
}

const tagIcons: Record<string, LucideIcon> = {
    Email: Mail,
    Payments: CreditCard,
    'E-signature': PenLine,
    Approval: CheckCircle2,
    Automation: Zap,
    Analytics: BarChart3,
    Calendar: Calendar,
    Workflow: LayoutTemplate,
    Tracking: Activity,
    Integration: Link2,
    Security: Shield,
    PDF: FileText,
    Reporting: PieChart,
    Verification: ShieldCheck,
    Notifications: Bell,
    'Multi-step': ListOrdered,
    Compliance: BadgeCheck,
}

export function tagIconFa(tag: string): LucideIcon {
    return tagIcons[tag] ?? Tag
}
